import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useTheme } from '../../contexts/ThemeContext';
import RunningScansIndicator from './RunningScansIndicator';
import SystemHealthBanner from './SystemHealthBanner';
import { useSubscriptionOptional } from '../../contexts/SubscriptionContext';
import UserProfileDropdown from './UserProfileDropdown';
import AlertBanner, { SubscriptionExpiredBanner, TrialBanner } from '../Common/AlertBanner';
import CarryForwardBanner from '../Common/CarryForwardBanner';
import { useClientSubscriptions, getSubscriptionBlockInfo, subscriptionBlockText } from '../../utils/subscriptionStatus';
import axios from 'axios';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentProject } = useProject();
  const { branding } = useSettings();
  const { effectiveTheme, toggleTheme } = useTheme();
  const subscription = useSubscriptionOptional();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const userInfoRef = useRef<HTMLDivElement>(null);
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(false);
  const [appVersion, setAppVersion] = useState('');

  // Fetch app version once
  useEffect(() => {
    axios.get('/api/v1/version').then(res => setAppVersion(res.data.version)).catch(() => {});
  }, []);

  // Set document title from branding settings
  useEffect(() => {
    if (branding.applicationName) {
      document.title = branding.applicationName;
    }
  }, [branding.applicationName]);

  // Get subscription status from context
  const subscriptionStatus = subscription?.subscriptionStatus || null;

  // Open admin submenu when on admin route
  useEffect(() => {
    if (location.pathname.startsWith('/admin')) {
      setIsAdminMenuOpen(true);
    }
  }, [location.pathname]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobileMenuOpen) {
        const sidebar = document.querySelector('.sidebar');
        const hamburger = document.querySelector('.hamburger-menu');
        if (sidebar && !sidebar.contains(event.target as Node) &&
            hamburger && !hamburger.contains(event.target as Node)) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  const handleUpgradeClick = () => {
    navigate('/account?tab=subscription');
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Check if we're in a project context (viewing a specific project)
  const isInProjectContext = currentProject !== null;
  const projectId = currentProject?.id;

  // Check user role
  const isClientUser = user?.role === 'client' || user?.role === 'client_admin';
  const isAdmin = user?.role === 'superuser';

  // Admin/superuser: surface when the CURRENTLY VIEWED project's client has an inactive
  // subscription (trial/subscription expired, suspended or canceled). The client's scheduled
  // scans are paused while blocked and resume automatically once the subscription is renewed.
  const { byClient: clientSubs } = useClientSubscriptions(isAdmin && isInProjectContext);
  const projectClientId = (currentProject as any)?.client_id as number | undefined;
  const projectClientBlock = getSubscriptionBlockInfo(
    isAdmin && projectClientId ? clientSubs[projectClientId] : null
  );

  const getPageTitle = () => {
    if (location.pathname === '/dashboard') return t('nav.dashboard');
    if (location.pathname === '/projects') return t('nav.projects');
    if (location.pathname === '/scans') return t('nav.scans');
    if (location.pathname === '/results') return t('nav.results');
    if (location.pathname === '/reports') return t('nav.reports');
    if (location.pathname === '/schedules') return t('nav.schedules');
    if (location.pathname === '/clients') return t('nav.clients');
    if (location.pathname === '/security') return t('security.dashboard');
    if (location.pathname === '/agents') return t('nav.agents');
    if (location.pathname === '/vulndata') return t('nav.vulnData');
    if (location.pathname === '/settings') return t('nav.settings');
    if (location.pathname === '/admin') return t('nav.admin');
    if (location.pathname === '/help') return t('nav.help', 'Help');
    if (location.pathname === '/account') return t('nav.account', 'Account');
    if (location.pathname === '/tickets') return t('nav.tickets', 'Tickets');
    if (location.pathname.startsWith('/tickets/')) return t('tickets.ticketDetails', 'Ticket Details');
    if (location.pathname.startsWith('/clients/')) return t('clients.clientDetails', 'Client Details');
    if (location.pathname.startsWith('/security/')) return t('security.dashboard');
    if (location.pathname.startsWith('/projects/') && location.pathname.includes('/overview')) return t('projects.overview');
    if (location.pathname.startsWith('/projects/') && location.pathname.includes('/targets')) return t('projects.targets', 'Project Targets');
    if (location.pathname.startsWith('/projects/') && location.pathname.includes('/scans')) return t('projects.scans', 'Project Scans');
    if (location.pathname.startsWith('/projects/') && location.pathname.includes('/results')) return t('projects.results', 'Project Results');
    if (location.pathname.startsWith('/projects/') && location.pathname.includes('/reports')) return t('projects.reports', 'Project Reports');
    if (location.pathname.startsWith('/projects/') && location.pathname.includes('/schedules')) return t('projects.schedules', 'Project Schedules');
    if (location.pathname.startsWith('/projects/') && !location.pathname.includes('/')) return t('projects.projectDetails', 'Project Details');
    if (location.pathname.includes('/scans/new')) return t('scans.newScan');
    if (location.pathname.startsWith('/scans/') && location.pathname !== '/scans') return t('scans.scanDetails', 'Scan Details');
    if (location.pathname.startsWith('/vulndata/')) {
      if (location.pathname.includes('/rules')) return t('vulnData.rules');
      if (location.pathname.includes('/databases')) return t('vulnData.databases');
      if (location.pathname.includes('/imports')) return t('vulnData.imports');
      if (location.pathname.includes('/config')) return t('vulnData.config');
      return t('nav.vulnData');
    }
    return branding.applicationName;
  };

  return (
    <div className="layout">
      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo" style={{ padding: '8px 0', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            {(() => {
              const logo = effectiveTheme === 'dark' && branding.applicationLogoDark
                ? branding.applicationLogoDark
                : branding.applicationLogo;
              return logo ? (
                <img
                  src={logo}
                  alt={branding.applicationName}
                  style={{ height: '60px', maxWidth: '200px', objectFit: 'contain' }}
                />
              ) : (
                <>
                  <i className="fas fa-shield-alt" style={{ color: 'var(--color-primary-400)', fontSize: '1.5rem' }}></i>
                  <span style={{ marginLeft: '8px', fontSize: '1.25rem', fontWeight: 600 }}>{branding.applicationName}</span>
                </>
              );
            })()}
          </div>
        </div>

        <nav className="sidebar-nav">
          <Link
            to="/dashboard"
            className={`nav-item ${isActiveRoute('/dashboard') ? 'active' : ''}`}
          >
            <i className="fas fa-tachometer-alt" style={{ color: isActiveRoute('/dashboard') ? 'var(--color-text-inverse)' : 'var(--color-primary-500)' }}></i>
            <span>{t('nav.dashboard')}</span>
          </Link>

          <Link
            to="/projects"
            className={`nav-item ${isActiveRoute('/projects') ? 'active' : ''}`}
          >
            <i className="fas fa-folder-open" style={{ color: isActiveRoute('/projects') ? 'var(--color-text-inverse)' : 'var(--color-primary-500)' }}></i>
            <span>{t('nav.projects')}</span>
          </Link>

          {/* Project Sub-menu - shown when a project is selected */}
          {isInProjectContext && projectId && (
            <div className="nav-submenu">
              <Link
                to={`/projects/${projectId}`}
                className={`nav-subitem ${location.pathname === `/projects/${projectId}` ? 'active' : ''}`}
              >
                <i className="fas fa-info-circle" style={{ color: location.pathname === `/projects/${projectId}` ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                <span>{t('projects.overview')}</span>
              </Link>
              <Link
                to={`/projects/${projectId}/targets`}
                className={`nav-subitem ${location.pathname.includes('/targets') ? 'active' : ''}`}
              >
                <i className="fas fa-crosshairs" style={{ color: location.pathname.includes('/targets') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                <span>{t('targets.title')}</span>
              </Link>
              <Link
                to={`/projects/${projectId}/scans`}
                className={`nav-subitem ${location.pathname.includes(`/projects/${projectId}/scans`) ? 'active' : ''}`}
              >
                <i className="fas fa-search" style={{ color: location.pathname.includes(`/projects/${projectId}/scans`) ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                <span>{t('scans.title')}</span>
              </Link>
              <Link
                to={`/projects/${projectId}/results`}
                className={`nav-subitem ${location.pathname.includes('/results') ? 'active' : ''}`}
              >
                <i className="fas fa-list-alt" style={{ color: location.pathname.includes('/results') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                <span>{t('results.title')}</span>
              </Link>
              <Link
                to={`/projects/${projectId}/reports`}
                className={`nav-subitem ${location.pathname.includes('/reports') ? 'active' : ''}`}
              >
                <i className="fas fa-file-alt" style={{ color: location.pathname.includes('/reports') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                <span>{t('reports.title')}</span>
              </Link>
              <Link
                to={`/projects/${projectId}/schedules`}
                className={`nav-subitem ${location.pathname.includes('/schedules') && !location.pathname.includes('/projects/') ? 'active' : location.pathname.includes(`/projects/${projectId}/schedules`) ? 'active' : ''}`}
              >
                <i className="fas fa-calendar-alt" style={{ color: location.pathname.includes(`/projects/${projectId}/schedules`) ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                <span>{t('nav.schedules')}</span>
              </Link>
              <Link
                to={`/projects/${projectId}/exposure`}
                className={`nav-subitem ${location.pathname.includes(`/projects/${projectId}/exposure`) ? 'active' : ''}`}
              >
                <i className="fas fa-chart-line" style={{ color: location.pathname.includes(`/projects/${projectId}/exposure`) ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                <span>{t('nav.exposure', 'Exposure Monitoring')}</span>
              </Link>
              <Link
                to={`/projects/${projectId}/findings`}
                className={`nav-subitem ${location.pathname.includes(`/projects/${projectId}/findings`) ? 'active' : ''}`}
              >
                <i className="fas fa-ban" style={{ color: location.pathname.includes(`/projects/${projectId}/findings`) ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                <span>{t('nav.findings', 'Suppressions')}</span>
              </Link>
              <Link
                to={`/projects/${projectId}/breach-monitoring`}
                className={`nav-subitem ${location.pathname.includes(`/projects/${projectId}/breach-monitoring`) ? 'active' : ''}`}
              >
                <i className="fas fa-user-secret" style={{ color: location.pathname.includes(`/projects/${projectId}/breach-monitoring`) ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                <span>{t('nav.breachMonitoring', 'Breach Monitoring')}</span>
              </Link>
              <Link
                to={`/projects/${projectId}/client-reviews`}
                className={`nav-subitem ${location.pathname.includes(`/projects/${projectId}/client-reviews`) ? 'active' : ''}`}
              >
                <i className="fas fa-clipboard-check" style={{ color: location.pathname.includes(`/projects/${projectId}/client-reviews`) ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                <span>{t('nav.clientReviews', 'Client Reviews')}</span>
              </Link>
              <Link
                to={`/projects/${projectId}/agents`}
                className={`nav-subitem ${location.pathname.includes(`/projects/${projectId}/agents`) ? 'active' : ''}`}
              >
                <i className="fas fa-robot" style={{ color: location.pathname.includes(`/projects/${projectId}/agents`) ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                <span>{t('nav.agents')}</span>
              </Link>
            </div>
          )}

          {/* Admin-only menu items */}
          {!isClientUser && (
            <>
              <Link
                to="/scans"
                className={`nav-item ${location.pathname === '/scans' || (location.pathname.startsWith('/scans/') && !location.pathname.includes('/projects/')) ? 'active' : ''}`}
              >
                <i className="fas fa-search" style={{ color: (location.pathname === '/scans' || (location.pathname.startsWith('/scans/') && !location.pathname.includes('/projects/'))) ? 'var(--color-text-inverse)' : 'var(--color-primary-500)' }}></i>
                <span>{t('scans.allScans', 'All Scans')}</span>
              </Link>

              <Link
                to="/recon"
                className={`nav-item ${isActiveRoute('/recon') ? 'active' : ''}`}
              >
                <i className="fas fa-binoculars" style={{ color: isActiveRoute('/recon') ? 'var(--color-text-inverse)' : 'var(--color-primary-500)' }}></i>
                <span>{t('nav.recon', 'Recon Scan')}</span>
              </Link>

              <Link
                to="/schedules"
                className={`nav-item ${isActiveRoute('/schedules') ? 'active' : ''}`}
              >
                <i className="fas fa-calendar-alt" style={{ color: isActiveRoute('/schedules') ? 'var(--color-text-inverse)' : 'var(--color-primary-500)' }}></i>
                <span>{t('nav.schedules')}</span>
              </Link>

              <Link
                to="/clients"
                className={`nav-item ${isActiveRoute('/clients') ? 'active' : ''}`}
              >
                <i className="fas fa-users" style={{ color: isActiveRoute('/clients') ? 'var(--color-text-inverse)' : 'var(--color-primary-500)' }}></i>
                <span>{t('nav.clients')}</span>
              </Link>

              <Link
                to="/security"
                className={`nav-item ${isActiveRoute('/security') ? 'active' : ''}`}
              >
                <i className="fas fa-shield-virus" style={{ color: isActiveRoute('/security') ? 'var(--color-text-inverse)' : 'var(--color-primary-500)' }}></i>
                <span>{t('security.dashboard')}</span>
              </Link>

              <Link
                to="/findings"
                className={`nav-item ${isActiveRoute('/findings') ? 'active' : ''}`}
              >
                <i className="fas fa-ban" style={{ color: isActiveRoute('/findings') ? 'var(--color-text-inverse)' : 'var(--color-primary-500)' }}></i>
                <span>{t('nav.findings', 'Finding Management')}</span>
              </Link>

              <Link
                to="/exposure"
                className={`nav-item ${isActiveRoute('/exposure') ? 'active' : ''}`}
              >
                <i className="fas fa-chart-line" style={{ color: isActiveRoute('/exposure') ? 'var(--color-text-inverse)' : 'var(--color-primary-500)' }}></i>
                <span>{t('nav.exposure', 'Exposure Monitoring')}</span>
              </Link>

              <Link
                to="/vulndata"
                className={`nav-item ${isActiveRoute('/vulndata') ? 'active' : ''}`}
              >
                <i className="fas fa-database" style={{ color: isActiveRoute('/vulndata') ? 'var(--color-text-inverse)' : 'var(--color-primary-500)' }}></i>
                <span>{t('nav.vulnData')}</span>
              </Link>

              <Link
                to="/client-reviews"
                className={`nav-item ${isActiveRoute('/client-reviews') ? 'active' : ''}`}
              >
                <i className="fas fa-clipboard-check" style={{ color: isActiveRoute('/client-reviews') ? 'var(--color-text-inverse)' : 'var(--color-primary-500)' }}></i>
                <span>{t('nav.clientReviews', 'Client Reviews')}</span>
              </Link>

              <Link
                to="/agents"
                className={`nav-item ${isActiveRoute('/agents') ? 'active' : ''}`}
              >
                <i className="fas fa-robot" style={{ color: isActiveRoute('/agents') ? 'var(--color-text-inverse)' : 'var(--color-primary-500)' }}></i>
                <span>{t('nav.agents')}</span>
              </Link>
            </>
          )}

          {/* Client-accessible menu items */}
          {isClientUser && (
            <>
              <Link
                to="/client-reviews"
                className={`nav-item ${isActiveRoute('/client-reviews') ? 'active' : ''}`}
              >
                <i className="fas fa-clipboard-check" style={{ color: isActiveRoute('/client-reviews') ? 'var(--color-text-inverse)' : 'var(--color-primary-500)' }}></i>
                <span>{t('nav.clientReviews', 'Client Reviews')}</span>
              </Link>
            </>
          )}

          {/* Items available to all users */}
          {!isClientUser && (
            <Link
              to="/reports"
              className={`nav-item ${isActiveRoute('/reports') ? 'active' : ''}`}
            >
              <i className="fas fa-file-alt" style={{ color: isActiveRoute('/reports') ? 'var(--color-text-inverse)' : 'var(--color-primary-500)' }}></i>
              <span>{t('nav.reports')}</span>
            </Link>
          )}

          {/* Tickets - accessible to all users */}
          <Link
            to="/tickets"
            className={`nav-item ${isActiveRoute('/tickets') ? 'active' : ''}`}
          >
            <i className="fas fa-ticket-alt" style={{ color: isActiveRoute('/tickets') ? 'var(--color-text-inverse)' : 'var(--color-primary-500)' }}></i>
            <span>{t('nav.tickets', 'Tickets')}</span>
          </Link>

          {/* Account link for client users - above Help */}
          {isClientUser && user?.client_id && (
            <Link
              to="/account"
              className={`nav-item ${isActiveRoute('/account') ? 'active' : ''}`}
            >
              <i className="fas fa-building" style={{ color: isActiveRoute('/account') ? 'var(--color-text-inverse)' : 'var(--color-primary-500)' }}></i>
              <span>{t('nav.account', 'Account')}</span>
            </Link>
          )}

          <Link
            to="/help"
            className={`nav-item ${isActiveRoute('/help') ? 'active' : ''}`}
          >
            <i className="fas fa-question-circle" style={{ color: isActiveRoute('/help') ? 'var(--color-text-inverse)' : 'var(--color-primary-500)' }}></i>
            <span>{t('nav.help', 'Help')}</span>
          </Link>

          {isAdmin && (
            <>
              <div
                className={`nav-item nav-item-expandable ${isActiveRoute('/admin') ? 'active' : ''}`}
                onClick={() => setIsAdminMenuOpen(!isAdminMenuOpen)}
              >
                <i className="fas fa-users-cog" style={{ color: isActiveRoute('/admin') ? 'var(--color-text-inverse)' : 'var(--color-primary-500)' }}></i>
                <span>{t('nav.admin')}</span>
                <i className={`fas fa-chevron-${isAdminMenuOpen ? 'down' : 'right'} submenu-arrow`} style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}></i>
              </div>
              {isAdminMenuOpen && (
                <div className="nav-submenu admin-submenu">
                  <Link to="/admin?tab=users" className={`nav-subitem ${location.search.includes('tab=users') || (location.pathname === '/admin' && !location.search) ? 'active' : ''}`}>
                    <i className="fas fa-users" style={{ color: location.search.includes('tab=users') || (location.pathname === '/admin' && !location.search) ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.users', 'Users')}</span>
                  </Link>
                  <Link to="/admin?tab=alert-center" className={`nav-subitem ${location.search.includes('tab=alert-center') ? 'active' : ''}`}>
                    <i className="fas fa-shield-alt" style={{ color: location.search.includes('tab=alert-center') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.alertCenter', 'Alert Center')}</span>
                  </Link>
                  <Link to="/admin?tab=threat-intelligence" className={`nav-subitem ${location.search.includes('tab=threat-intelligence') ? 'active' : ''}`}>
                    <i className="fas fa-crosshairs" style={{ color: location.search.includes('tab=threat-intelligence') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.threatIntelligence', 'Threat Intelligence')}</span>
                  </Link>
                  <Link to="/admin?tab=adhoc" className={`nav-subitem ${location.search.includes('tab=adhoc') ? 'active' : ''}`}>
                    <i className="fas fa-satellite-dish" style={{ color: location.search.includes('tab=adhoc') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.adhoc', 'Ad-hoc Checks')}</span>
                  </Link>
                  <Link to="/admin?tab=settings" className={`nav-subitem ${location.search.includes('tab=settings') ? 'active' : ''}`}>
                    <i className="fas fa-cogs" style={{ color: location.search.includes('tab=settings') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.settings', 'Settings')}</span>
                  </Link>
                  <Link to="/admin?tab=report-templates" className={`nav-subitem ${location.search.includes('tab=report-templates') ? 'active' : ''}`}>
                    <i className="fas fa-file-invoice" style={{ color: location.search.includes('tab=report-templates') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.reportTemplates', 'Report Templates')}</span>
                  </Link>
                  <Link to="/admin?tab=report-blocks" className={`nav-subitem ${location.search.includes('tab=report-blocks') ? 'active' : ''}`}>
                    <i className="fas fa-th" style={{ color: location.search.includes('tab=report-blocks') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.reportBlocks', 'Report Blocks')}</span>
                  </Link>
                  <Link to="/admin?tab=email-templates" className={`nav-subitem ${location.search.includes('tab=email-templates') ? 'active' : ''}`}>
                    <i className="fas fa-mail-bulk" style={{ color: location.search.includes('tab=email-templates') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.emailTemplates', 'Email Templates')}</span>
                  </Link>
                  <Link to="/admin?tab=recon" className={`nav-subitem ${location.search.includes('tab=recon') ? 'active' : ''}`}>
                    <i className="fas fa-search" style={{ color: location.search.includes('tab=recon') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.recon', 'Recon Settings')}</span>
                  </Link>
                  <Link to="/admin?tab=plans" className={`nav-subitem ${location.search.includes('tab=plans') ? 'active' : ''}`}>
                    <i className="fas fa-layer-group" style={{ color: location.search.includes('tab=plans') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.plans', 'Subscription Plans')}</span>
                  </Link>
                  <Link to="/admin?tab=client-subscriptions" className={`nav-subitem ${location.search.includes('tab=client-subscriptions') ? 'active' : ''}`}>
                    <i className="fas fa-credit-card" style={{ color: location.search.includes('tab=client-subscriptions') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.clientSubscriptions', 'Client Subscriptions')}</span>
                  </Link>
                  <Link to="/admin?tab=registrations" className={`nav-subitem ${location.search.includes('tab=registrations') ? 'active' : ''}`}>
                    <i className="fas fa-user-plus" style={{ color: location.search.includes('tab=registrations') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.registrations', 'Registrations')}</span>
                  </Link>
                  <Link to="/admin?tab=gtc" className={`nav-subitem ${location.search.includes('tab=gtc') ? 'active' : ''}`}>
                    <i className="fas fa-file-contract" style={{ color: location.search.includes('tab=gtc') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.gtc', 'Terms & Conditions')}</span>
                  </Link>
                  <Link to="/admin?tab=backups" className={`nav-subitem ${location.search.includes('tab=backups') ? 'active' : ''}`}>
                    <i className="fas fa-database" style={{ color: location.search.includes('tab=backups') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.backups', 'Backups')}</span>
                  </Link>
                  <Link to="/admin?tab=archives" className={`nav-subitem ${location.search.includes('tab=archives') ? 'active' : ''}`}>
                    <i className="fas fa-archive" style={{ color: location.search.includes('tab=archives') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.archives', 'Scan Archives')}</span>
                  </Link>
                  <Link to="/admin?tab=default-creds" className={`nav-subitem ${location.search.includes('tab=default-creds') ? 'active' : ''}`}>
                    <i className="fas fa-key" style={{ color: location.search.includes('tab=default-creds') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.defaultCreds', 'Default Credentials')}</span>
                  </Link>
                  <Link to="/admin?tab=notifications" className={`nav-subitem ${location.search.includes('tab=notifications') ? 'active' : ''}`}>
                    <i className="fas fa-envelope" style={{ color: location.search.includes('tab=notifications') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.notifications', 'Notification Logs')}</span>
                  </Link>
                  <Link to="/admin?tab=notification-subs" className={`nav-subitem ${location.search.includes('tab=notification-subs') ? 'active' : ''}`}>
                    <i className="fas fa-bell" style={{ color: location.search.includes('tab=notification-subs') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.notificationSubs', 'Notification Subscriptions')}</span>
                  </Link>
                  <Link to="/admin?tab=weekly-digest" className={`nav-subitem ${location.search.includes('tab=weekly-digest') ? 'active' : ''}`}>
                    <i className="fas fa-calendar-week" style={{ color: location.search.includes('tab=weekly-digest') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.weeklyDigest', 'Weekly Digest')}</span>
                  </Link>
                  <Link to="/admin?tab=integrations" className={`nav-subitem ${location.search.includes('tab=integrations') ? 'active' : ''}`}>
                    <i className="fas fa-plug" style={{ color: location.search.includes('tab=integrations') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.integrations', 'Integrations')}</span>
                  </Link>
                  <Link to="/admin?tab=partner-keys" className={`nav-subitem ${location.search.includes('tab=partner-keys') ? 'active' : ''}`}>
                    <i className="fas fa-handshake" style={{ color: location.search.includes('tab=partner-keys') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.partnerKeys', 'Partner Keys')}</span>
                  </Link>
                  <Link to="/admin?tab=cost-allocation" className={`nav-subitem ${location.search.includes('tab=cost-allocation') ? 'active' : ''}`}>
                    <i className="fas fa-chart-pie" style={{ color: location.search.includes('tab=cost-allocation') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.costAllocation', 'API Cost Allocation')}</span>
                  </Link>
                  <Link to="/admin?tab=reports" className={`nav-subitem ${location.search.includes('tab=reports') ? 'active' : ''}`}>
                    <i className="fas fa-file-alt" style={{ color: location.search.includes('tab=reports') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.reports', 'Report Review')}</span>
                  </Link>
                  <Link to="/admin?tab=exploits" className={`nav-subitem ${location.search.includes('tab=exploits') ? 'active' : ''}`}>
                    <i className="fas fa-bug" style={{ color: location.search.includes('tab=exploits') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.exploits', 'CVE Exploits')}</span>
                  </Link>
                  <Link to="/admin?tab=ai-logs" className={`nav-subitem ${location.search.includes('tab=ai-logs') ? 'active' : ''}`}>
                    <i className="fas fa-robot" style={{ color: location.search.includes('tab=ai-logs') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.aiLogs', 'AI Logs')}</span>
                  </Link>
                  <Link to="/admin?tab=translations" className={`nav-subitem ${location.search.includes('tab=translations') ? 'active' : ''}`}>
                    <i className="fas fa-language" style={{ color: location.search.includes('tab=translations') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.translations', 'Finding Texts')}</span>
                  </Link>
                  <Link to="/admin?tab=web-paths" className={`nav-subitem ${location.search.includes('tab=web-paths') ? 'active' : ''}`}>
                    <i className="fas fa-route" style={{ color: location.search.includes('tab=web-paths') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.webPaths', 'Web Path Checks')}</span>
                  </Link>
                  <Link to="/admin?tab=help-editor" className={`nav-subitem ${location.search.includes('tab=help-editor') ? 'active' : ''}`}>
                    <i className="fas fa-question-circle" style={{ color: location.search.includes('tab=help-editor') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.helpEditor', 'Help Editor')}</span>
                  </Link>
                  <Link to="/admin?tab=info" className={`nav-subitem ${location.search.includes('tab=info') ? 'active' : ''}`}>
                    <i className="fas fa-info-circle" style={{ color: location.search.includes('tab=info') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.info', 'System Info')}</span>
                  </Link>
                  <Link to="/admin?tab=performance" className={`nav-subitem ${location.search.includes('tab=performance') ? 'active' : ''}`}>
                    <i className="fas fa-tachometer-alt" style={{ color: location.search.includes('tab=performance') ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.performance', 'Performance')}</span>
                  </Link>
                  <Link to="/admin/engine-limits" className={`nav-subitem ${location.pathname === '/admin/engine-limits' ? 'active' : ''}`}>
                    <i className="fas fa-sliders-h" style={{ color: location.pathname === '/admin/engine-limits' ? 'var(--color-text-inverse)' : 'var(--color-text-muted)' }}></i>
                    <span>{t('admin.tabs.engineLimits', 'Engine Limits')}</span>
                  </Link>
                </div>
              )}
            </>
          )}
        </nav>

        <div className="sidebar-footer" style={{ position: 'relative' }}>
          <div
            className="user-info"
            ref={userInfoRef}
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            style={{ cursor: 'pointer' }}
          >
            <div className="user-avatar">
              <i className="fas fa-user" style={{ color: 'var(--color-primary-400)' }}></i>
            </div>
            <div className="user-details">
              <span className="user-name">{user?.username || user?.email || t('common.user', 'User')}</span>
              <span className="user-role">
                {user?.role === 'superuser' ? t('admin.administrator', 'Administrator') : t('admin.securityAnalyst', 'Security Analyst')}
              </span>
            </div>
            <i className={`fas fa-chevron-${isProfileOpen ? 'down' : 'up'}`} style={{ color: 'var(--color-text-tertiary)', marginLeft: 'auto' }}></i>
          </div>
          <UserProfileDropdown
            isOpen={isProfileOpen}
            onClose={() => setIsProfileOpen(false)}
            anchorRef={userInfoRef}
          />
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <header className="main-header">
          <div className="header-content">
            {/* Mobile hamburger menu - CSS controls visibility based on screen size */}
            <button
              className="hamburger-menu"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              type="button"
            >
              {isMobileMenuOpen ? (
                <i className="fas fa-times" style={{ fontSize: '1.25rem' }}></i>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              )}
            </button>
            {/* Mobile logo - CSS controls visibility based on screen size */}
            <div className="mobile-logo">
              {branding.applicationLogo ? (
                <img
                  src={branding.applicationLogo}
                  alt={branding.applicationName}
                  style={{ height: '32px', maxWidth: '120px', objectFit: 'contain' }}
                />
              ) : (
                <span style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--color-text-primary)' }}>
                  <i className="fas fa-shield-alt" style={{ color: 'var(--color-primary-400)', marginRight: '6px' }}></i>
                  {branding.applicationName}
                </span>
              )}
            </div>
            <h1 className="page-title">{getPageTitle()}</h1>
            <div className="header-actions">
              {isAdmin && <RunningScansIndicator />}
              <button
                className="theme-toggle"
                onClick={toggleTheme}
                title={effectiveTheme === 'dark' ? t('settings.switchToLight', 'Switch to light mode') : t('settings.switchToDark', 'Switch to dark mode')}
                type="button"
              >
                <i className={`fas fa-${effectiveTheme === 'dark' ? 'sun' : 'moon'}`}></i>
              </button>
            </div>
          </div>
        </header>

        <div className="content-wrapper">
          {/* Self-monitoring health banner (admins only) — surfaces operational problems */}
          {isAdmin && <SystemHealthBanner />}

          {/* Carry-forward decisions banner (only on project pages, only when pending) */}
          {(() => {
            const m = location.pathname.match(/^\/projects\/(\d+)(?:\/|$)/);
            return <CarryForwardBanner projectId={m ? Number(m[1]) : null} />;
          })()}

          {/* Admin: the viewed project's client has an inactive subscription → scans paused */}
          {isAdmin && projectClientBlock.blocked && location.pathname.match(/^\/projects\/(\d+)(?:\/|$)/) && (
            <div className="subscription-banner-container" style={{ marginBottom: '20px' }}>
              {(() => {
                const txt = subscriptionBlockText(projectClientBlock, (currentProject as any)?.client?.name);
                return (
                  <AlertBanner
                    type="error"
                    title={txt.title}
                    message={txt.message}
                    dismissible={false}
                    actionText={t('subscriptions.manageSubscription', 'Manage subscription')}
                    actionLink="/admin?tab=client-subscriptions"
                  />
                );
              })()}
            </div>
          )}

          {/* Subscription Status Banners */}
          {subscriptionStatus && !subscriptionStatus.isActive && subscriptionStatus.reason && (
            <div className="subscription-banner-container" style={{ marginBottom: '20px' }}>
              <SubscriptionExpiredBanner
                reason={subscriptionStatus.reason}
                onUpgrade={handleUpgradeClick}
                trialEndDate={subscriptionStatus.trialEndDate}
              />
            </div>
          )}

          {/* Trial Warning Banner - only show if trial is active but ending soon */}
          {subscriptionStatus &&
           subscriptionStatus.isActive &&
           subscriptionStatus.isTrial &&
           subscriptionStatus.trialDaysRemaining <= 7 &&
           !trialBannerDismissed && (
            <div className="subscription-banner-container" style={{ marginBottom: '20px' }}>
              <TrialBanner
                daysRemaining={subscriptionStatus.trialDaysRemaining}
                onUpgrade={handleUpgradeClick}
                onDismiss={() => setTrialBannerDismissed(true)}
              />
            </div>
          )}

          {children}
        </div>

        {/* Fixed version badge - bottom right of every page */}
        {appVersion && (
          <div className="app-version-badge">
            v{appVersion} | {window.location.hostname === 'app.exposiq.ch' ? 'PROD' : window.location.hostname.includes('test') ? 'TEST' : 'DEV'}
          </div>
        )}
      </main>
    </div>
  );
};

export default Layout;
